import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconFilePlus, IconGavel, IconUsers, IconChevronLeft, IconChevronRight, IconCalendar, IconListCheck } from '@tabler/icons-react';
import Chart from 'react-apexcharts';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

// Meses do ano
const months = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

// Anos disponíveis (últimos 5 anos)
const currentYear = new Date().getFullYear();
const minYear = currentYear - 4;
const years = Array.from({ length: 5 }, (_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

// Componente de Card Resumo Compacto
const SummaryCard = ({ title, value, subtitle, icon: Icon, color, loading }) => (
  <Card sx={{ height: '100%', bgcolor: `${color}.lighter` || 'background.paper' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
            {title}
          </Typography>
          {loading ? (
            <CircularProgress size={20} sx={{ mt: 0.5 }} />
          ) : (
            <Typography variant="h3" sx={{ mt: 0.5, color: `${color}.dark` }}>
              {value}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}.light`, color: `${color}.dark` }}>
          <Icon size={28} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardsPage = () => {
  const theme = useTheme();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [viewMode, setViewMode] = useState(0); // 0: Gráficos (padrão), 1: Tabelas

  // Verifica se está no mês/ano atual (não pode avançar além)
  const isCurrentMonthYear = selectedMonth === currentMonth && selectedYear === currentYear;
  // Verifica se está no primeiro mês/ano disponível (não pode recuar além)
  const isFirstMonthYear = selectedMonth === 1 && selectedYear === minYear;

  // Funções de navegação de mês
  const handlePreviousMonth = () => {
    if (isFirstMonthYear) return;
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (isCurrentMonthYear) return;
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Estados dos dashboards
  const [loadingConsultant, setLoadingConsultant] = useState(false);
  const [errorConsultant, setErrorConsultant] = useState(null);
  const [dataConsultant, setDataConsultant] = useState({ data: [], total_contracts: 0, without_consultant: 0 });

  const [loadingClosedCpfs, setLoadingClosedCpfs] = useState(false);
  const [errorClosedCpfs, setErrorClosedCpfs] = useState(null);
  const [dataClosedCpfs, setDataClosedCpfs] = useState({ data: [], total_cpfs: 0, without_consultant: 0 });

  const [loadingToFileAndFiled, setLoadingToFileAndFiled] = useState(false);
  const [errorToFileAndFiled, setErrorToFileAndFiled] = useState(null);
  const [dataToFileAndFiled, setDataToFileAndFiled] = useState({ data: [], total_to_file: 0, total_filed: 0 });

  const [loadingLawyer, setLoadingLawyer] = useState(false);
  const [errorLawyer, setErrorLawyer] = useState(null);
  const [dataLawyer, setDataLawyer] = useState({ data: [], total_contracts: 0, without_lawyer: 0 });

  const [loadingAddedByUser, setLoadingAddedByUser] = useState(false);
  const [errorAddedByUser, setErrorAddedByUser] = useState(null);
  const [dataAddedByUser, setDataAddedByUser] = useState({ data: [], total_contracts: 0 });

  const [loadingFiled, setLoadingFiled] = useState(false);
  const [errorFiled, setErrorFiled] = useState(null);
  const [dataFiled, setDataFiled] = useState({ data: [], total_filed: 0, without_lawyer: 0 });

  const [loadingByCategory, setLoadingByCategory] = useState(false);
  const [errorByCategory, setErrorByCategory] = useState(null);
  const [dataByCategory, setDataByCategory] = useState({ data: [], total_valid_contracts: 0 });
  const [categoryFilterMode, setCategoryFilterMode] = useState('monthly'); // 'monthly' ou 'all'

  const [loadingByState, setLoadingByState] = useState(false);
  const [errorByState, setErrorByState] = useState(null);
  const [dataByState, setDataByState] = useState({ data: [], total_valid_contracts: 0 });
  const [stateFilterMode, setStateFilterMode] = useState('monthly'); // 'monthly' ou 'all'

  // Fetch functions
  const fetchDataConsultant = useCallback(async () => {
    setLoadingConsultant(true);
    setErrorConsultant(null);
    try {
      const response = await fetch(`/backend/dashboard/contracts-by-sales-consultant/?year=${selectedYear}&month=${selectedMonth}`);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataConsultant(await response.json());
    } catch (err) {
      setErrorConsultant(err.message);
    } finally {
      setLoadingConsultant(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchDataClosedCpfs = useCallback(async () => {
    setLoadingClosedCpfs(true);
    setErrorClosedCpfs(null);
    try {
      const response = await fetch(`/backend/dashboard/closed-cpfs-by-sales-consultant/?year=${selectedYear}&month=${selectedMonth}`);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataClosedCpfs(await response.json());
    } catch (err) {
      setErrorClosedCpfs(err.message);
    } finally {
      setLoadingClosedCpfs(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchDataToFileAndFiled = useCallback(async () => {
    setLoadingToFileAndFiled(true);
    setErrorToFileAndFiled(null);
    try {
      const response = await fetch(`/backend/dashboard/contracts-to-file-and-filed/?year=${selectedYear}&month=${selectedMonth}`);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataToFileAndFiled(await response.json());
    } catch (err) {
      setErrorToFileAndFiled(err.message);
    } finally {
      setLoadingToFileAndFiled(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchDataLawyer = useCallback(async () => {
    setLoadingLawyer(true);
    setErrorLawyer(null);
    try {
      const response = await fetch(`/backend/dashboard/contracts-by-lawyer/?year=${selectedYear}&month=${selectedMonth}`);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataLawyer(await response.json());
    } catch (err) {
      setErrorLawyer(err.message);
    } finally {
      setLoadingLawyer(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchDataAddedByUser = useCallback(async () => {
    setLoadingAddedByUser(true);
    setErrorAddedByUser(null);
    try {
      const response = await fetch(`/backend/dashboard/contracts-added-by-user/?year=${selectedYear}&month=${selectedMonth}`);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataAddedByUser(await response.json());
    } catch (err) {
      setErrorAddedByUser(err.message);
    } finally {
      setLoadingAddedByUser(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchDataFiled = useCallback(async () => {
    setLoadingFiled(true);
    setErrorFiled(null);
    try {
      const response = await fetch(`/backend/dashboard/contracts-filed/?year=${selectedYear}&month=${selectedMonth}`);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataFiled(await response.json());
    } catch (err) {
      setErrorFiled(err.message);
    } finally {
      setLoadingFiled(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchDataByCategory = useCallback(async () => {
    setLoadingByCategory(true);
    setErrorByCategory(null);
    try {
      // Se o modo for 'all', não envia year e month
      const url = categoryFilterMode === 'all' 
        ? '/backend/dashboard/valid-contracts-by-category/'
        : `/backend/dashboard/valid-contracts-by-category/?year=${selectedYear}&month=${selectedMonth}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataByCategory(await response.json());
    } catch (err) {
      setErrorByCategory(err.message);
    } finally {
      setLoadingByCategory(false);
    }
  }, [selectedYear, selectedMonth, categoryFilterMode]);

  const fetchDataByState = useCallback(async () => {
    setLoadingByState(true);
    setErrorByState(null);
    try {
      // Se o modo for 'all', não envia year e month
      const url = stateFilterMode === 'all' 
        ? '/backend/dashboard/valid-contracts-by-state/'
        : `/backend/dashboard/valid-contracts-by-state/?year=${selectedYear}&month=${selectedMonth}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Erro ao carregar dados');
      setDataByState(await response.json());
    } catch (err) {
      setErrorByState(err.message);
    } finally {
      setLoadingByState(false);
    }
  }, [selectedYear, selectedMonth, stateFilterMode]);

  useEffect(() => {
    fetchDataConsultant();
    fetchDataClosedCpfs();
    fetchDataToFileAndFiled();
    fetchDataLawyer();
    fetchDataAddedByUser();
    fetchDataFiled();
    fetchDataByCategory();
    fetchDataByState();
  }, [fetchDataConsultant, fetchDataClosedCpfs, fetchDataToFileAndFiled, fetchDataLawyer, fetchDataAddedByUser, fetchDataFiled, fetchDataByCategory, fetchDataByState]);

  // Configurações de gráficos - cores do tema do dashboard
  const chartColors = [
    '#2196f3',                      // Primary Main - Azul
    '#673ab7',                      // Secondary Main - Roxo
    '#00e676',                      // Success Main - Verde
    '#d84315',                      // Orange Dark - Laranja
    '#f44336',                      // Error Main - Vermelho
    '#1565c0',                      // Primary 800 - Azul escuro
    '#5e35b1',                      // Secondary Dark - Roxo escuro
    '#00c853',                      // Success Dark - Verde escuro
    '#ffab91',                      // Orange Main - Laranja claro
    '#1e88e5',                      // Primary Dark - Azul médio
    '#7c4dff',                      // Secondary alternativo
    '#69f0ae',                      // Success 200 - Verde claro
    '#4527a0',                      // Secondary 800 - Violeta
    '#90caf9',                      // Primary 200 - Azul claro
    '#b39ddb',                      // Secondary 200 - Lavanda
  ];

  const createChartOptions = (categories, color, title = 'Contratos', valueLabel = 'contrato(s)') => ({
    chart: { 
      type: 'bar', 
      toolbar: { 
        show: true,
        export: {
          csv: {
            columnDelimiter: ';',
            headerCategory: 'Categoria',
            headerValue: 'Quantidade',
            filename: title,
          },
        },
      },
    },
    plotOptions: { bar: { horizontal: true, barHeight: '50%', distributed: true, borderRadius: 4 } },
    colors: chartColors,
    dataLabels: { enabled: true, formatter: (val) => val, style: { fontSize: '12px', fontWeight: 'bold', colors: ['#fff'] } },
    xaxis: { categories, title: { text: 'Quantidade' } },
    yaxis: { labels: { maxWidth: 200 } },
    legend: { show: false },
    tooltip: { y: { formatter: (val) => val + ' ' + valueLabel } },
  });

  const createPieChartOptions = (labels, colors, title = 'Contratos') => ({
    chart: {
      type: 'pie',
      toolbar: {
        show: true,
        export: {
          csv: {
            columnDelimiter: ';',
            headerCategory: 'Categoria',
            headerValue: 'Quantidade',
            filename: title,
          },
        },
      },
    },
    labels,
    colors,
    legend: { position: 'bottom' },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} contrato(s)`,
      },
    },
  });

  // Renderiza tabela detalhada
  const renderTable = (data, columns, idKey, loading, error) => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
    if (data.length === 0) return <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>Nenhum dado encontrado</Typography>;
    
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align || 'left'} sx={{ fontWeight: 'bold' }}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={row[idKey] || `row-${index}`} hover>
                <TableCell align="center">{index + 1}</TableCell>
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'}>
                    {col.render ? col.render(row, data) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Grid container spacing={gridSpacing}>
      {/* Header com Filtros e Modo de Visualização */}
      <Grid item xs={12}>
        <MainCard>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton onClick={handlePreviousMonth} size="small" color="primary" disabled={isFirstMonthYear}>
                <IconChevronLeft size={20} />
              </IconButton>
              <TextField
                select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ minWidth: 130 }}
                size="small"
              >
                {months.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{ minWidth: 100 }}
                size="small"
              >
                {years.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <IconButton onClick={handleNextMonth} size="small" color="primary" disabled={isCurrentMonthYear}>
                <IconChevronRight size={20} />
              </IconButton>
            </Box>
            <Tabs value={viewMode} onChange={(e, v) => setViewMode(v)} sx={{ minHeight: 36 }}>
              <Tab label="Gráficos" sx={{ minHeight: 36, py: 0 }} />
              <Tab label="Tabelas" sx={{ minHeight: 36, py: 0 }} />
            </Tabs>
          </Box>
        </MainCard>
      </Grid>

      {/* MODO GRÁFICOS (padrão) */}
      {viewMode === 0 && (
        <>
          {/* Cards de Resumo */}
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="Contratos Incluídos no Mês"
              value={dataConsultant.total_contracts}
              icon={IconFilePlus}
              color="primary"
              loading={loadingConsultant}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="Contratos Protocolados no Mês"
              value={dataFiled.total_filed}
              icon={IconGavel}
              color="info"
              loading={loadingFiled}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="CPFs Fechados no Mês"
              value={dataClosedCpfs.total_cpfs}
              icon={IconUsers}
              color="error"
              loading={loadingClosedCpfs}
            />
          </Grid>

          {/* Gráficos 2x2 */}
          <Grid item xs={12} md={6}>
            <MainCard title="Contratos Válidos por Consultor de Vendas">
              {loadingConsultant ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorConsultant ? (
                <Alert severity="error">{errorConsultant}</Alert>
              ) : dataConsultant.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <Chart
                  options={createChartOptions(dataConsultant.data.map(i => i.sales_consultant_name || 'Sem nome'), theme.palette.primary.main, 'Contratos_por_Consultor')}
                  series={[{ name: 'Contratos', data: dataConsultant.data.map(i => i.count) }]}
                  type="bar"
                  height={Math.max(250, dataConsultant.data.length * 40)}
                />
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title="Contratos por Advogado da Inicial">
              {loadingLawyer ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorLawyer ? (
                <Alert severity="error">{errorLawyer}</Alert>
              ) : dataLawyer.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <Chart
                  options={createChartOptions(dataLawyer.data.map(i => i.lawyer_name || 'Sem nome'), theme.palette.success.main, 'Contratos_por_Advogado')}
                  series={[{ name: 'Contratos', data: dataLawyer.data.map(i => i.count) }]}
                  type="bar"
                  height={Math.max(250, dataLawyer.data.length * 40)}
                />
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title="Contratos por Usuário que Adicionou">
              {loadingAddedByUser ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorAddedByUser ? (
                <Alert severity="error">{errorAddedByUser}</Alert>
              ) : dataAddedByUser.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <Chart
                  options={createChartOptions(dataAddedByUser.data.map(i => i.user_name || 'Sem nome'), theme.palette.warning.main, 'Contratos_por_Usuario')}
                  series={[{ name: 'Contratos', data: dataAddedByUser.data.map(i => i.count) }]}
                  type="bar"
                  height={Math.max(250, dataAddedByUser.data.length * 40)}
                />
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title="Contratos Protocolados por Advogado">
              {loadingFiled ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorFiled ? (
                <Alert severity="error">{errorFiled}</Alert>
              ) : dataFiled.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <Chart
                  options={createChartOptions(dataFiled.data.map(i => i.lawyer_name || 'Sem nome'), theme.palette.info.main, 'Contratos_Protocolados')}
                  series={[{ name: 'Protocolados', data: dataFiled.data.map(i => i.count) }]}
                  type="bar"
                  height={Math.max(250, dataFiled.data.length * 40)}
                />
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  Contratos Válidos por Categoria
                  <ToggleButtonGroup
                    value={categoryFilterMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setCategoryFilterMode(newMode)}
                    size="small"
                    sx={{ height: 32 }}
                  >
                    <ToggleButton value="monthly" sx={{ px: 1.5, py: 0.5 }}>
                      <IconCalendar size={16} style={{ marginRight: 4 }} />
                      <Typography variant="caption">Mês</Typography>
                    </ToggleButton>
                    <ToggleButton value="all" sx={{ px: 1.5, py: 0.5 }}>
                      <IconListCheck size={16} style={{ marginRight: 4 }} />
                      <Typography variant="caption">Todos</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              }
            >
              {loadingByCategory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorByCategory ? (
                <Alert severity="error">{errorByCategory}</Alert>
              ) : dataByCategory.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <>
                  {categoryFilterMode === 'all' && (
                    <Chip 
                      label={`Total de ${dataByCategory.total_valid_contracts} contratos válidos`} 
                      color="secondary" 
                      size="small" 
                      sx={{ mb: 2 }} 
                    />
                  )}
                  <Chart
                    options={createChartOptions(dataByCategory.data.map(i => i.category_name || 'Sem categoria'), theme.palette.secondary.main, 'Contratos_por_Categoria')}
                    series={[{ name: 'Contratos Válidos', data: dataByCategory.data.map(i => i.count) }]}
                    type="bar"
                    height={Math.max(250, dataByCategory.data.length * 40)}
                  />
                </>
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h3">Contratos Válidos por UF</Typography>
                  <ToggleButtonGroup
                    value={stateFilterMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setStateFilterMode(newMode)}
                    size="small"
                    sx={{ height: 32 }}
                  >
                    <ToggleButton value="monthly" sx={{ px: 1.5, py: 0.5 }}>
                      <IconCalendar size={16} style={{ marginRight: 4 }} />
                      <Typography variant="caption">Mês</Typography>
                    </ToggleButton>
                    <ToggleButton value="all" sx={{ px: 1.5, py: 0.5 }}>
                      <IconListCheck size={16} style={{ marginRight: 4 }} />
                      <Typography variant="caption">Todos</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              }
            >
              {loadingByState ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorByState ? (
                <Alert severity="error">{errorByState}</Alert>
              ) : dataByState.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <>
                  {stateFilterMode === 'all' && (
                    <Chip 
                      label={`Total de ${dataByState.total_valid_contracts} contratos válidos`} 
                      color="success" 
                      size="small" 
                      sx={{ mb: 2 }} 
                    />
                  )}
                  <Chart
                    options={createChartOptions(dataByState.data.map(i => i.state || 'Sem UF'), theme.palette.success.main, 'Contratos_por_UF')}
                    series={[{ name: 'Contratos Válidos', data: dataByState.data.map(i => i.count) }]}
                    type="bar"
                    height={Math.max(250, dataByState.data.length * 40)}
                  />
                </>
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title={`CPFs Fechados por Consultor de Vendas (${dataClosedCpfs.total_cpfs} CPFs)`}>
              {loadingClosedCpfs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorClosedCpfs ? (
                <Alert severity="error">{errorClosedCpfs}</Alert>
              ) : dataClosedCpfs.data.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <Chart
                  options={createChartOptions(
                    dataClosedCpfs.data.map(i => i.sales_consultant_name || 'Sem nome'),
                    theme.palette.error.main,
                    'CPFs_Fechados_por_Consultor',
                    'CPF(s)'
                  )}
                  series={[{ name: 'CPFs Fechados', data: dataClosedCpfs.data.map(i => i.count) }]}
                  type="bar"
                  height={Math.max(250, dataClosedCpfs.data.length * 40)}
                />
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title="Contratos a Ajuizar e Ajuizados">
              {loadingToFileAndFiled ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : errorToFileAndFiled ? (
                <Alert severity="error">{errorToFileAndFiled}</Alert>
              ) : (dataToFileAndFiled.total_to_file + dataToFileAndFiled.total_filed) === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 4, textAlign: 'center' }}>Nenhum dado</Typography>
              ) : (
                <Chart
                  options={createPieChartOptions(
                    dataToFileAndFiled.data.map(i => `${i.label} (${i.count})`),
                    ['#90caf9', '#1565c0'],
                    'Contratos_a_Ajuizar_e_Ajuizados'
                  )}
                  series={dataToFileAndFiled.data.map(i => i.count)}
                  type="pie"
                  height={300}
                />
              )}
            </MainCard>
          </Grid>
        </>
      )}

      {/* MODO TABELAS */}
      {viewMode === 1 && (
        <>
          <Grid item xs={12} md={6}>
            <MainCard title={`Contratos Válidos por Consultor de Vendas (${dataConsultant.total_contracts} contratos)`}>
              {renderTable(
                dataConsultant.data,
                [
                  { key: 'sales_consultant_name', label: 'Consultor' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataConsultant.total_contracts > 0 ? ((row.count / dataConsultant.total_contracts) * 100).toFixed(1) : 0}%` },
                ],
                'sales_consultant_id',
                loadingConsultant,
                errorConsultant
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title={`Advogados da Inicial (${dataLawyer.total_contracts} contratos)`}>
              {renderTable(
                dataLawyer.data,
                [
                  { key: 'lawyer_name', label: 'Advogado' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataLawyer.total_contracts > 0 ? ((row.count / dataLawyer.total_contracts) * 100).toFixed(1) : 0}%` },
                ],
                'lawyer_id',
                loadingLawyer,
                errorLawyer
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title={`Usuários que Adicionaram (${dataAddedByUser.total_contracts} contratos)`}>
              {renderTable(
                dataAddedByUser.data,
                [
                  { key: 'user_name', label: 'Usuário' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataAddedByUser.total_contracts > 0 ? ((row.count / dataAddedByUser.total_contracts) * 100).toFixed(1) : 0}%` },
                ],
                'user_id',
                loadingAddedByUser,
                errorAddedByUser
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title={`Protocolados por Advogado (${dataFiled.total_filed} contratos)`}>
              {renderTable(
                dataFiled.data,
                [
                  { key: 'lawyer_name', label: 'Advogado' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataFiled.total_filed > 0 ? ((row.count / dataFiled.total_filed) * 100).toFixed(1) : 0}%` },
                ],
                'lawyer_id',
                loadingFiled,
                errorFiled
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  
                    Contratos Válidos por Categoria ({dataByCategory.total_valid_contracts} contratos)
                    {categoryFilterMode === 'all' && <Chip label="Todos" size="small" color="secondary" sx={{ ml: 1 }} />}
                  
                  <ToggleButtonGroup
                    value={categoryFilterMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setCategoryFilterMode(newMode)}
                    size="small"
                    sx={{ height: 28 }}
                  >
                    <ToggleButton value="monthly" sx={{ px: 1, py: 0.25 }}>
                      <IconCalendar size={14} style={{ marginRight: 4 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Mês</Typography>
                    </ToggleButton>
                    <ToggleButton value="all" sx={{ px: 1, py: 0.25 }}>
                      <IconListCheck size={14} style={{ marginRight: 4 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Todos</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              }
            >
              {renderTable(
                dataByCategory.data,
                [
                  { key: 'category_name', label: 'Categoria' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataByCategory.total_valid_contracts > 0 ? ((row.count / dataByCategory.total_valid_contracts) * 100).toFixed(1) : 0}%` },
                ],
                'category_id',
                loadingByCategory,
                errorByCategory
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h4">
                    Contratos Válidos por UF ({dataByState.total_valid_contracts} contratos)
                    {stateFilterMode === 'all' && <Chip label="Todos" size="small" color="success" sx={{ ml: 1 }} />}
                  </Typography>
                  <ToggleButtonGroup
                    value={stateFilterMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setStateFilterMode(newMode)}
                    size="small"
                    sx={{ height: 28 }}
                  >
                    <ToggleButton value="monthly" sx={{ px: 1, py: 0.25 }}>
                      <IconCalendar size={14} style={{ marginRight: 4 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Mês</Typography>
                    </ToggleButton>
                    <ToggleButton value="all" sx={{ px: 1, py: 0.25 }}>
                      <IconListCheck size={14} style={{ marginRight: 4 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Todos</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              }
            >
              {renderTable(
                dataByState.data,
                [
                  { key: 'state', label: 'UF' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataByState.total_valid_contracts > 0 ? ((row.count / dataByState.total_valid_contracts) * 100).toFixed(1) : 0}%` },
                ],
                'state',
                loadingByState,
                errorByState
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title={`CPFs Fechados por Consultor de Vendas (${dataClosedCpfs.total_cpfs} CPFs)`}>
              {renderTable(
                dataClosedCpfs.data,
                [
                  { key: 'sales_consultant_name', label: 'Consultor' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  { key: 'percent', label: '%', align: 'center', render: (row) => `${dataClosedCpfs.total_cpfs > 0 ? ((row.count / dataClosedCpfs.total_cpfs) * 100).toFixed(1) : 0}%` },
                ],
                'sales_consultant_id',
                loadingClosedCpfs,
                errorClosedCpfs
              )}
            </MainCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MainCard title={`Contratos a Ajuizar e Ajuizados (${dataToFileAndFiled.total_to_file + dataToFileAndFiled.total_filed} contratos)`}>
              {renderTable(
                dataToFileAndFiled.data,
                [
                  { key: 'label', label: 'Tipo' },
                  { key: 'count', label: 'Qtd.', align: 'center', render: (row) => <strong>{row.count}</strong> },
                  {
                    key: 'percent',
                    label: '%',
                    align: 'center',
                    render: (row) => {
                      const total = dataToFileAndFiled.total_to_file + dataToFileAndFiled.total_filed;
                      return `${total > 0 ? ((row.count / total) * 100).toFixed(1) : 0}%`;
                    },
                  },
                ],
                'key',
                loadingToFileAndFiled,
                errorToFileAndFiled
              )}
            </MainCard>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default DashboardsPage;
